import type { IParameter } from '../../create-api-context';

const IMPORT_MAX_OBJECT_DEPTH = 50;

const KNOWN_TYPES = new Set([
  'string',
  'number',
  'integer',
  'boolean',
  'array',
  'object',
  'variable',
  'file',
]);

export type JsonToParametersOptions = {
  location?: string;
  rootName?: string;
};

export type JsonToParametersResult = {
  parameters: Record<string, IParameter>;
  warnings: string[];
};

export function jsonToParameters(
  value: unknown,
  options: JsonToParametersOptions = {},
): JsonToParametersResult {
  const location = options.location || 'body';
  const rootName = options.rootName || 'data';
  const warnings: string[] = [];

  if (isParameterSchema(value)) {
    return { parameters: forceLocation(value, location), warnings };
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const parameters: Record<string, IParameter> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      parameters[key] = valueToParameter(key, child, location, 0, warnings);
    });
    return { parameters, warnings };
  }

  return {
    parameters: {
      [rootName]: valueToParameter(rootName, value, location, 0, warnings),
    },
    warnings,
  };
}

function isParameterSchema(value: unknown): value is Record<string, IParameter> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.values(value as Record<string, unknown>);
  if (entries.length === 0) return false;
  return entries.every((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const param = item as Partial<IParameter>;
    if (!param.Type || !KNOWN_TYPES.has(String(param.Type).toLowerCase())) return false;
    return (
      typeof param.Required === 'boolean' ||
      typeof param.Location === 'string' ||
      param.Properties != null ||
      param.Items != null
    );
  });
}

function forceLocation(
  params: Record<string, IParameter>,
  location: string,
): Record<string, IParameter> {
  const result: Record<string, IParameter> = {};
  Object.entries(params).forEach(([key, param]) => {
    result[key] = {
      ...param,
      Name: param.Name || key,
      Location: location,
      Properties: param.Properties ? forceLocation(param.Properties, location) : param.Properties,
      Items: param.Items ? { ...param.Items, Location: location } : param.Items,
    };
  });
  return result;
}

function valueToParameter(
  name: string,
  value: unknown,
  location: string,
  objectDepth: number,
  warnings: string[],
): IParameter {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (objectDepth >= IMPORT_MAX_OBJECT_DEPTH) {
      warnings.push(
        `Nested object "${name}" exceeded ${IMPORT_MAX_OBJECT_DEPTH} levels and was truncated.`,
      );
      return {
        Name: name,
        Type: 'object',
        Description: '',
        Required: true,
        Location: location,
        Properties: {},
      };
    }
    const properties: Record<string, IParameter> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      properties[key] = valueToParameter(key, child, location, objectDepth + 1, warnings);
    });
    return {
      Name: name,
      Type: 'object',
      Description: '',
      Required: true,
      Location: location,
      Properties: properties,
    };
  }

  if (Array.isArray(value)) {
    const first = value.length > 0 ? value[0] : '';
    const items = valueToParameter('', first, location, objectDepth, warnings);
    items.Name = '';
    return {
      Name: name,
      Type: 'array',
      Description: '',
      Required: true,
      Location: location,
      DefaultValue: value,
      Items: items,
    };
  }

  const inferred = inferJsonScalar(value);
  return {
    Name: name,
    Type: inferred.type,
    Description: '',
    Required: true,
    Location: location,
    DefaultValue: inferred.value,
  };
}

function inferJsonScalar(value: unknown): { type: string; value: unknown } {
  if (typeof value === 'boolean') return { type: 'boolean', value };
  if (typeof value === 'number') {
    return { type: Number.isInteger(value) ? 'integer' : 'number', value };
  }
  return { type: 'string', value: value == null ? '' : String(value) };
}
